import { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { dischargeResident } from '@/lib/api/residents';

export interface DischargeTarget {
  id: string;
  user?: {
    name: string | null;
    patientCode?: string | null;
  } | null;
  room?: { roomNumber: string } | null;
}

interface Props {
  resident: DischargeTarget | null;
  onClose: () => void;
}

/**
 * 퇴소 처리 확인 다이얼로그.
 * BGT user.name이 거의 모두 null이므로 환자번호를 함께 표시해 식별 모호성 제거.
 */
export function DischargeConfirmDialog({ resident, onClose }: Props) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      if (!resident) throw new Error('no resident');
      return dischargeResident(resident.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof AxiosError) {
        const msg = (err.response?.data as { message?: string } | undefined)?.message;
        setError(msg ?? '퇴소 처리 중 오류가 발생했습니다');
      } else {
        setError('알 수 없는 오류가 발생했습니다');
      }
    },
  });

  const identifyParts: string[] = [];
  if (resident?.room?.roomNumber) identifyParts.push(`${resident.room.roomNumber}호`);
  if (resident?.user?.patientCode) identifyParts.push(`환자번호 ${resident.user.patientCode}`);
  if (resident?.user?.name) identifyParts.push(resident.user.name);
  const identify = identifyParts.length > 0 ? identifyParts.join(' · ') : '이 입소자';

  return (
    <Dialog open={!!resident} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>퇴소 처리</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {identify}를 퇴소 처리합니다.
          <br />
          취소할 수 없습니다. 계속하시겠습니까?
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? '처리 중…' : '퇴소'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
